import { useCallback, useState } from 'react';
import ReportTemplateModal from '@/modules/common/components/modals/ReportTemplateModal.tsx';
import { useGetReportsTemplatesQuery } from '@/modules/admin-dashboard/queries/reports-templates.ts';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { PlusBlueIcon } from 'ui/icons';
import { ReportTemplateResponse } from 'compliance-api';

type baseProps = {
  label?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  isClearable?: boolean;
  error?: string;
  enableCreation?: boolean;
  cacheUniqs?: unknown[];
};

type singleProps = {
  isMulti: false;
  onChange: (options: SelectPaginateOption<number>) => void;
  defaultValue?: SelectPaginateOption<number>;
  value?: SelectPaginateOption<number> | null;
};

type multiProps = {
  isMulti: true;
  onChange: (options: SelectPaginateOption<number>[]) => void;
  defaultValue?: SelectPaginateOption<number>[];
  value?: SelectPaginateOption<number>[];
};

type ReportTemplatesSelectPaginateProps = baseProps & (singleProps | multiProps);

const ReportTemplatesSelectPaginate = ({
  value,
  label,
  error,
  isMulti,
  className,
  isClearable,
  placeholder,
  defaultValue,
  disabled,
  enableCreation,
  cacheUniqs,
  onChange,
}: ReportTemplatesSelectPaginateProps) => {
  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [cacheUniq, setCacheUniq] = useState(Math.random());
  const { fetchNextPage } = useGetReportsTemplatesQuery({ page: 1, page_size: 15 }, true);

  const handleAsyncLoadRegisterOptions = useCallback(
    async (page: number, search?: string) => {
      const templatesData = await fetchNextPage({ pageParam: { page, search } });
      const templates = templatesData?.data?.pages?.at(-1)?.data || [];

      return templates.map(({ id, name }) => ({ label: name, value: id }));
    },
    [fetchNextPage],
  );

  const addedLastOption = enableCreation
    ? {
        label: (
          <div className="flex gap-2">
            <img src={PlusBlueIcon} alt="add" />
            <span>Add template</span>
          </div>
        ),
        onClick: () => {
          setIsCreationOpen(true);
        },
      }
    : undefined;

  const handleTemplateAdded = useCallback(
    (res: ReportTemplateResponse) => {
      setCacheUniq(Math.random());

      const newValue = { label: res.name, value: res.id };
      if (isMulti) {
        onChange([...(value || []), newValue]);
      } else {
        onChange(newValue);
      }
    },
    [isMulti, onChange, value],
  );

  return (
    <>
      <SelectPaginate
        key={[cacheUniq, ...(cacheUniqs || [])].join(',')}
        cacheUniqs={[cacheUniq, ...(cacheUniqs || [])]}
        isMulti={isMulti}
        disabled={disabled}
        defaultValue={defaultValue}
        className={className}
        label={label}
        error={error}
        value={value}
        isClearable={isClearable}
        placeholder={placeholder}
        onAsync={handleAsyncLoadRegisterOptions}
        onChange={options => onChange(options as any)}
        addedLastOption={addedLastOption}
      />
      {isCreationOpen && (
        <ReportTemplateModal
          open={isCreationOpen}
          onClose={() => setIsCreationOpen(false)}
          onSuccess={handleTemplateAdded}
        />
      )}
    </>
  );
};

export default ReportTemplatesSelectPaginate;
